import { ButtonPrimary } from '@/components/button'
import { PasswordInput } from '@/components/input'
import { useResetPassword } from '@/services'
import ChangePasswordValidationSchema from '@/utils/schema/ChangePasswordValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

export type ChangePasswordForm = {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

/**
 * The change-password sub-section of Password & Security. Rendered bare: the section page owns the
 * dashboard shell, the heading and the sub-navigation, and mounts this as its index route.
 */
export const ChangePassword = () => {
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ChangePasswordForm>({
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onTouched',
    resolver: yupResolver(ChangePasswordValidationSchema),
  })

  const { mutate: resetPassword } = useResetPassword()

  const onSubmit: SubmitHandler<ChangePasswordForm> = (data) => {
    if (isValid) {
      const payload = {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      }
      resetPassword(payload, {
        onSuccess: (data: any) => {
          toast.success(data.message)
          reset()
        },
        onError: (errors: any) => {
          toast.error(errors.response.data.message)
        },
      })
    }
  }

  return (
    <section className="flex flex-col gap-y-4">
      <div className="flex flex-col gap-y-1">
        <h2 className="text-lg font-semibold text-white">{t('user.changePass')}</h2>
        <p className="text-white/70 text-sm">
          {t(
            'user.changePassHint',
            'Choose a password you do not use anywhere else. Changing it signs you out of your other sessions.'
          )}
        </p>
      </div>
      <form className="flex flex-col gap-y-4" onSubmit={handleSubmit(onSubmit)}>
        <PasswordInput
          label={t('common.oldPass')}
          placeholder={t('common.oldPass')}
          {...register('oldPassword')}
          error={errors.oldPassword?.message}
          autoFocus
        />

        <PasswordInput
          label={t('common.newPass')}
          placeholder={t('common.newPass')}
          {...register('newPassword')}
          error={errors.newPassword?.message}
        />

        <PasswordInput
          label={t('common.confirmPass')}
          placeholder={t('common.confirmPass')}
          {...register('confirmPassword')}
          error={errors.confirmPassword?.message}
        />

        <div className="flex justify-end">
          <ButtonPrimary type="submit" className="px-6 focus:outline-none" title={t('common.save')} />
        </div>
      </form>
    </section>
  )
}
